import { useState, useEffect, useCallback } from 'react';
import { PhoneNumber, PurchaseNumberRequest, PurchaseNumberResponse, TelephonyProviderInfo } from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const usePhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiClient.get('/telephony/phone-numbers/');
      const result = await handleApiResult<PhoneNumber[]>(
        response,
        'Failed to fetch phone numbers'
      );

      if (result.success && result.data) {
        setPhoneNumbers(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch phone numbers');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch phone numbers');
      setError(processedErrors.generalError || 'Failed to fetch phone numbers');
    } finally {
      setLoading(false);
    }
  }, []);

  const purchasePhoneNumber = async (
    data: PurchaseNumberRequest
  ): Promise<PurchaseNumberResponse> => {
    try {
      const payload: Record<string, string> = {};
      if (data.country_code) payload.country_code = data.country_code;
      if (data.block_id) payload.block_id = data.block_id;

      const response = await ApiClient.post('/telephony/phone-numbers/purchase_number/', payload);
      const result = await handleApiResult<PhoneNumber>(
        response,
        'Failed to purchase phone number'
      );

      if (result.success && result.data) {
        setPhoneNumbers(prev => [...prev, result.data!]);
        return {
          success: true,
          phoneNumber: result.data,
        };
      } else {
        // Handle field-specific errors (e.g., country_code validation)
        const errorMessage =
          result.errors?.fieldErrors?.country_code ||
          result.errors?.generalError ||
          result.errors?.nonFieldErrors?.[0] ||
          'Failed to purchase phone number';
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to purchase phone number');
      return {
        success: false,
        error: processedErrors.generalError,
      };
    }
  };

  const fetchProviderInfo = async (): Promise<TelephonyProviderInfo> => {
    try {
      const response = await ApiClient.get('/telephony/phone-numbers/provider_info/');
      const result = await handleApiResult<TelephonyProviderInfo>(
        response,
        'Failed to fetch provider info'
      );
      if (result.success && result.data) {
        return result.data;
      }
    } catch {
      // fallback
    }
    return { provider: 'twilio' };
  };

  const deletePhoneNumber = async (id: string): Promise<boolean> => {
    try {
      const response = await ApiClient.delete(`/telephony/phone-numbers/${id}/`);

      if (response.ok) {
        setPhoneNumbers(prev => prev.filter(phone => phone.id !== id));
        setError(null);
        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  return {
    phoneNumbers,
    loading,
    error,
    purchasePhoneNumber,
    fetchProviderInfo,
    deletePhoneNumber,
    fetchPhoneNumbers,
  };
};
